import { useCallback, type Dispatch } from "react";
import i18n from "@/i18n";

import {
  buildPartitionDownloads,
  revokePartitionDownloads,
} from "./externalRegisterImportCsv";
import { sessionDraftAdapter } from "./externalRegisterImportDraft";
import { readFileAsText } from "./externalRegisterImportFile";
import {
  buildImportHistorySummary,
  importHistoryAdapter,
} from "../history/externalRegisterImportHistory";
import {
  buildAggregatedCommitResponse,
  buildCommitRequest,
  canApproveReviewRow,
  buildSimulatedCommitPreviewRows,
  buildSimulatedCommitChunkResponse,
  getBulkApprovableRows,
  getCommitBatchDelayMs,
  getCommitRowIdBatches,
  getCommitRowIds,
  getOpenReviewRows,
  getRowsMissingRequiredCandidateSelection,
  type ImportApprovalBasis,
  type ImportApprovalThreshold,
  type ImportStage,
} from "./externalRegisterImportModel";
import type {
  ExternalRegisterImportAction,
  ExternalRegisterImportState,
} from "./externalRegisterImportReducer";
import type {
  ExternalRegisterImportAdapter,
  ImportCommitResponse,
  ImportCommitRequest,
  ImportMapping,
  ImportReviewFilter,
} from "./externalRegisterImportTypes";

export type ExternalRegisterImportController = {
  setCsvText: (csvText: string) => void;
  loadCsvFile: (file: File) => Promise<void>;
  parseCsv: () => Promise<void>;
  setHeaderMode: (hasHeader: boolean) => void;
  setMappingField: (field: keyof ImportMapping, value: string) => void;
  validatePreview: () => Promise<void>;
  approveRow: (rowId: string) => void;
  approveEligibleRows: (threshold: ImportApprovalThreshold, basis?: ImportApprovalBasis) => void;
  excludeRow: (rowId: string) => void;
  excludeRows: (rowIds: string[]) => void;
  selectCandidate: (rowId: string, patientId: string) => void;
  commitImport: () => Promise<void>;
  simulateCommitImport: () => Promise<void>;
  cancelImport: () => void;
  dismissHistory: (id: string) => void;
  setActiveStage: (stage: ImportStage) => void;
  setActiveFilter: (filter: ImportReviewFilter) => void;
  setPage: (updater: number | ((current: number) => number)) => void;
};

export const useExternalRegisterImportController = ({
  adapter,
  commitProgressDelayMs,
  dispatch,
  mappingErrors,
  onCommitRequest,
  reviewReadonly,
  state,
}: {
  adapter: ExternalRegisterImportAdapter;
  commitProgressDelayMs: number | null;
  dispatch: Dispatch<ExternalRegisterImportAction>;
  mappingErrors: string[];
  onCommitRequest?: (request: ImportCommitRequest) => void;
  reviewReadonly: boolean;
  state: ExternalRegisterImportState;
}): ExternalRegisterImportController => {
  const setCsvText = useCallback((csvText: string) => {
    if (reviewReadonly) return;
    dispatch({ type: "setCsvText", csvText });
  }, [dispatch, reviewReadonly]);

  const cancelImport = useCallback(() => {
    sessionDraftAdapter.clear();
    revokePartitionDownloads(state.partitionDownloads);
    dispatch({ type: "reset" });
  }, [dispatch, state.partitionDownloads]);

  const setHeaderMode = useCallback((hasHeader: boolean) => {
    if (reviewReadonly) return;
    dispatch({ type: "setHeaderMode", hasHeader });
  }, [dispatch, reviewReadonly]);

  const setMappingField = useCallback((field: keyof ImportMapping, value: string) => {
    if (reviewReadonly) return;
    dispatch({ type: "setMappingField", field, value });
  }, [dispatch, reviewReadonly]);

  const setActiveFilter = useCallback((activeFilter: ImportReviewFilter) => {
    dispatch({ type: "setActiveFilter", activeFilter });
  }, [dispatch]);

  const setPage = useCallback((page: number | ((current: number) => number)) => {
    dispatch({ type: "setPage", page });
  }, [dispatch]);

  const loadCsvFile = useCallback(async (file: File) => {
    if (reviewReadonly) return;
    dispatch({ type: "loadFileStart" });
    try {
      const csvText = await readFileAsText(file);
      dispatch({
        type: "loadFileSuccess",
        csvText,
        sourceFileName: file.name,
        sourceFileSize: file.size,
      });
    } catch (err) {
      dispatch({ type: "loadFileFailure", errorMessage: i18n.t("externalRegister.import.controller.errors.readCsvFile") });
    }
  }, [dispatch, reviewReadonly]);

  const parseCsv = useCallback(async () => {
    if (reviewReadonly) return;
    if (!state.csvText.trim()) return;
    dispatch({ type: "parseStart" });
    try {
      const response = await adapter.parse({
        clientImportId: state.clientImportId,
        csvText: state.csvText,
      });
      dispatch({ type: "parseSuccess", response });
    } catch (err) {
      dispatch({ type: "parseFailure", errorMessage: i18n.t("externalRegister.import.controller.errors.parseCsv") });
    }
  }, [adapter, dispatch, reviewReadonly, state.clientImportId, state.csvText]);

  const validatePreview = useCallback(async () => {
    if (reviewReadonly) return;
    if (!state.parseResponse) return;
    if (state.hasHeader === null) return;
    if (state.parseResponse.parseErrors.length) {
      dispatch({ type: "setErrorMessage", errorMessage: i18n.t("externalRegister.import.controller.errors.fixParseErrors") });
      dispatch({ type: "setActiveStage", activeStage: "parse" });
      return;
    }
    if (mappingErrors.length > 0) {
      dispatch({ type: "setErrorMessage", errorMessage: i18n.t("externalRegister.import.controller.errors.mappingIncomplete") });
      return;
    }

    dispatch({ type: "validateStart" });
    try {
      const response = await adapter.validate({
        clientImportId: state.clientImportId,
        csvText: state.csvText,
        hasHeader: state.hasHeader,
        mapping: state.mapping,
      });
      dispatch({ type: "validateSuccess", response });
    } catch (err) {
      dispatch({ type: "validateFailure", errorMessage: i18n.t("externalRegister.import.controller.errors.validationFailed") });
    }
  }, [adapter, dispatch, mappingErrors.length, reviewReadonly, state.clientImportId, state.csvText, state.hasHeader, state.mapping, state.parseResponse]);

  const prepareCommitRequest = useCallback((): ImportCommitRequest | null => {
    if (reviewReadonly) return null;
    if (!state.parseResponse || state.hasHeader === null) return null;
    if (state.previewRows.length === 0) return null;
    const commitRowIds = getCommitRowIds({
      previewRows: state.previewRows,
      approvedRowIds: state.approvedRowIds,
      excludedRowIds: state.excludedRowIds,
    });
    if (commitRowIds.length === 0) {
      dispatch({ type: "setErrorMessage", errorMessage: i18n.t("externalRegister.import.controller.errors.noRowsReady") });
      return null;
    }
    const openReviewRows = getOpenReviewRows({
      previewRows: state.previewRows,
      approvedRowIds: state.approvedRowIds,
      excludedRowIds: state.excludedRowIds,
    });
    if (openReviewRows.length > 0) {
      dispatch({ type: "setErrorMessage", errorMessage: i18n.t("externalRegister.import.controller.errors.reviewIncomplete") });
      dispatch({ type: "setActiveFilter", activeFilter: "needsReview" });
      return null;
    }
    const blockedRows = getRowsMissingRequiredCandidateSelection({
      rows: state.previewRows,
      approvedRowIds: state.approvedRowIds,
      selectedPatientIds: state.selectedPatientIds,
    });
    if (blockedRows.length > 0) {
      dispatch({ type: "setErrorMessage", errorMessage: i18n.t("externalRegister.import.controller.errors.candidatesRequired") });
      dispatch({ type: "setActiveFilter", activeFilter: "needsReview" });
      return null;
    }

    return buildCommitRequest({
      clientImportId: state.clientImportId,
      sourceFingerprint: state.parseResponse.sourceFingerprint,
      csvText: state.csvText,
      hasHeader: state.hasHeader,
      mapping: state.mapping,
      previewRows: state.previewRows,
      approvedRowIds: state.approvedRowIds,
      excludedRowIds: state.excludedRowIds,
      selectedPatientIds: state.selectedPatientIds,
    });
  }, [
    dispatch,
    reviewReadonly,
    state.approvedRowIds,
    state.clientImportId,
    state.csvText,
    state.excludedRowIds,
    state.hasHeader,
    state.mapping,
    state.parseResponse,
    state.previewRows,
    state.selectedPatientIds,
  ]);

  const runCommit = useCallback(async ({ simulate }: { simulate: boolean }) => {
    const request = prepareCommitRequest();
    if (!request) return;

    const progressPreviewRows = simulate ? buildSimulatedCommitPreviewRows() : state.previewRows;
    const progressCommitRowIds = simulate
      ? progressPreviewRows.map((row) => row.rowId)
      : request.commitRowIds ?? [];
    const commitRowIdBatches = getCommitRowIdBatches(progressCommitRowIds);
    const batchResponses: Array<{ batchIndex: number; response: ImportCommitResponse }> = [];
    revokePartitionDownloads(state.partitionDownloads);
    dispatch({ type: "setPartitionDownloads", partitionDownloads: [] });
    dispatch({ type: "commitStart" });
    dispatch({ type: "setActiveStage", activeStage: "report" });
    dispatch({
      type: "commitProgress",
      response: buildAggregatedCommitResponse({
        request,
        previewRows: progressPreviewRows,
        commitRowIdBatches,
        batchResponses,
        simulated: simulate,
      }),
    });

    let finalResponse = buildAggregatedCommitResponse({
      request,
      previewRows: progressPreviewRows,
      commitRowIdBatches,
      batchResponses,
      simulated: simulate,
    });

    try {
      for (let batchIndex = 0; batchIndex < commitRowIdBatches.length; batchIndex += 1) {
        const batchRequest = {
          ...request,
          commitRowIds: commitRowIdBatches[batchIndex],
        };
        dispatch({
          type: "commitProgress",
          response: buildAggregatedCommitResponse({
            request,
            previewRows: progressPreviewRows,
            commitRowIdBatches,
            batchResponses,
            activeBatchIndex: batchIndex,
            simulated: simulate,
          }),
        });
        onCommitRequest?.(batchRequest);

        if (simulate) {
          batchResponses.push({
            batchIndex,
            response: buildSimulatedCommitChunkResponse({
              request: batchRequest,
              previewRows: progressPreviewRows,
              commitRowIds: commitRowIdBatches[batchIndex],
            }),
          });
        } else {
          const responses = await adapter.commit(batchRequest);
          const response = responses.at(-1);
          if (!response) {
            throw new Error("Commit batch did not return a response.");
          }
          if (response.status === "failed") {
            const failureMessage = response.rows.find((row) => row.errorMessage)?.errorMessage
              || i18n.t("externalRegister.import.controller.errors.batchFailed");
            finalResponse = buildAggregatedCommitResponse({
              request,
              previewRows: progressPreviewRows,
              commitRowIdBatches,
              batchResponses,
              failedBatchIndex: batchIndex,
              failureMessage,
              simulated: simulate,
            });
            dispatch({ type: "commitProgress", response: finalResponse });
            dispatch({ type: "setErrorMessage", errorMessage: failureMessage });
            return;
          }
          batchResponses.push({ batchIndex, response });
        }

        finalResponse = buildAggregatedCommitResponse({
          request,
          previewRows: progressPreviewRows,
          commitRowIdBatches,
          batchResponses,
          simulated: simulate,
        });
        dispatch({ type: "commitProgress", response: finalResponse });

        if (finalResponse.status === "failed") break;
        if (batchIndex < commitRowIdBatches.length - 1) {
          const delayMs = getCommitBatchDelayMs(commitProgressDelayMs);
          if (delayMs > 0) {
            await new Promise((resolve) => window.setTimeout(resolve, delayMs));
          }
        }
      }
      if (finalResponse?.status === "completed_with_errors") {
        dispatch({ type: "commitCompletedWithErrors", response: finalResponse });
      }
      dispatch({
        type: "setPartitionDownloads",
        partitionDownloads: buildPartitionDownloads({
          csvText: state.csvText,
          hasHeader: request.hasHeader,
          previewRows: state.previewRows,
          commitResponse: finalResponse,
          excludedRowIds: state.excludedRowIds,
        }),
      });
      if (finalResponse && !simulate) {
        dispatch({ type: "setHistorySummaries", historySummaries: importHistoryAdapter.add(buildImportHistorySummary(finalResponse)) });
      }
      if (!simulate && finalResponse?.status === "completed") {
        sessionDraftAdapter.clear();
        dispatch({ type: "clearSensitiveDraftAfterCommit" });
      }
    } catch (err) {
      const failedBatchIndex = Math.min(batchResponses.length, Math.max(0, commitRowIdBatches.length - 1));
      finalResponse = buildAggregatedCommitResponse({
        request,
        previewRows: progressPreviewRows,
        commitRowIdBatches,
        batchResponses,
        failedBatchIndex,
        failureMessage: i18n.t("externalRegister.import.controller.errors.batchFailed"),
        simulated: simulate,
      });
      dispatch({ type: "commitProgress", response: finalResponse });
      dispatch({ type: "setErrorMessage", errorMessage: i18n.t("externalRegister.import.controller.errors.commitFailed") });
    }
  }, [
    adapter,
    commitProgressDelayMs,
    dispatch,
    onCommitRequest,
    prepareCommitRequest,
    state.csvText,
    state.hasHeader,
    state.partitionDownloads,
    state.previewRows,
  ]);

  const commitImport = useCallback(async () => {
    await runCommit({ simulate: false });
  }, [runCommit]);

  const simulateCommitImport = useCallback(async () => {
    await runCommit({ simulate: true });
  }, [runCommit]);

  const approveRow = useCallback((rowId: string) => {
    if (reviewReadonly) return;
    const row = state.previewRows.find((candidate) => candidate.rowId === rowId);
    if (row && !canApproveReviewRow(row, state.selectedPatientIds[rowId])) {
      dispatch({ type: "setErrorMessage", errorMessage: i18n.t("externalRegister.import.controller.errors.approveWithoutCandidate") });
      dispatch({ type: "setActiveFilter", activeFilter: "needsReview" });
      return;
    }
    dispatch({ type: "approveRow", rowId });
  }, [dispatch, reviewReadonly, state.previewRows, state.selectedPatientIds]);

  const approveEligibleRows = useCallback((threshold: ImportApprovalThreshold, basis?: ImportApprovalBasis) => {
    if (reviewReadonly) return;
    const rows = getBulkApprovableRows({
      rows: state.previewRows,
      threshold,
      basis,
      approvedRowIds: state.approvedRowIds,
      excludedRowIds: state.excludedRowIds,
      selectedPatientIds: state.selectedPatientIds,
    });
    if (rows.length === 0) {
      dispatch({ type: "setErrorMessage", errorMessage: i18n.t("externalRegister.import.controller.errors.bulkApproveNoEligible") });
      return;
    }
    dispatch({ type: "approveRows", rowIds: rows.map((row) => row.rowId) });
  }, [
    dispatch,
    reviewReadonly,
    state.approvedRowIds,
    state.excludedRowIds,
    state.previewRows,
    state.selectedPatientIds,
  ]);

  const excludeRow = useCallback((rowId: string) => {
    if (reviewReadonly) return;
    dispatch({ type: "excludeRow", rowId });
  }, [dispatch, reviewReadonly]);

  const excludeRows = useCallback((rowIds: string[]) => {
    if (reviewReadonly) return;
    dispatch({ type: "excludeRows", rowIds });
  }, [dispatch, reviewReadonly]);

  const selectCandidate = useCallback((rowId: string, patientId: string) => {
    if (reviewReadonly) return;
    dispatch({ type: "selectCandidate", rowId, patientId });
  }, [dispatch, reviewReadonly]);

  const dismissHistory = useCallback((id: string) => {
    dispatch({ type: "setHistorySummaries", historySummaries: importHistoryAdapter.dismiss(id) });
  }, [dispatch]);

  return {
    setCsvText,
    loadCsvFile,
    parseCsv,
    setHeaderMode,
    setMappingField,
    validatePreview,
    approveRow,
    approveEligibleRows,
    excludeRow,
    excludeRows,
    selectCandidate,
    commitImport,
    simulateCommitImport,
    cancelImport,
    dismissHistory,
    setActiveStage: (activeStage) => dispatch({ type: "setActiveStage", activeStage }),
    setActiveFilter,
    setPage,
  };
};
